import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', to: '/' },
    { name: 'About', to: '/about' },
    { name: 'Services', to: '/services' },
    { name: 'Products', to: '/products' },
    { name: 'Blog', to: '/blog' },
  ];

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Logo (top-left) */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="/images/quarkpower-logo.svg"
              alt="Quark Power"
              className="h-12 w-auto"
            />
          </Link>

          {/* Center: desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.to}
                className={`text-base font-semibold transition-colors whitespace-nowrap ${
                  isActive(link.to)
                    ? 'text-emerald-700'
                    : (isScrolled ? 'text-gray-700 hover:text-green-600' : 'text-gray-800 hover:text-green-600')
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right: CTA (desktop) + Menu button (mobile) */}
          <div className="flex items-center gap-3">
            <div className="hidden md:block">
              <Link
                to="/contact"
                className="inline-flex items-center px-7 py-3 bg-emerald-600 text-white text-base font-semibold rounded-lg hover:bg-emerald-700 transition-all duration-300 whitespace-nowrap shadow-lg shadow-emerald-600/30"
              >
                Contact
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>

            <button
              className="md:hidden p-2 cursor-pointer"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <i
                className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl ${
                  isScrolled ? 'text-gray-800' : 'text-gray-800'
                }`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.to}
                className="block px-4 py-3 text-base font-semibold text-gray-700 hover:bg-gray-50 hover:text-green-600 transition-colors whitespace-nowrap"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="px-4 pt-3">
              <Link
                to="/contact"
                className="inline-flex w-full items-center justify-center px-6 py-3 bg-emerald-600 text-white text-base font-semibold rounded-lg hover:bg-emerald-700 transition-all duration-300 shadow-lg shadow-emerald-600/30"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </motion.div>
        )}
      </nav>
    </motion.header>
  );
};

export default Navigation;